import React, { useEffect, useState, useCallback, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate, Link } from 'react-router-dom';
import {
    Newspaper,
    MessageSquare,
    TrendingUp,
    ChevronRight,
    Loader2,
    Plus,
    Eye,
    Activity,
    Sparkles,
    Bell,
    X,
    User,
    FileText,
    Clock,
    Mail,
    Phone,
    AlertCircle
} from 'lucide-react';
import AdminLayout from '../components/admin/AdminLayout';

const MESSAGES_KEY = 'siteMessages';

const AdminDashboardPage = () => {
    const { user, loading: authLoading } = useAuth();
    const navigate = useNavigate();
    const [stats, setStats] = useState({
        news: 0,
        contacts: 0,
        ads: 0,
        unread_contacts: 0
    });
    const [loading, setLoading] = useState(true);
    const [lastUpdate, setLastUpdate] = useState(new Date());
    const [recentMessages, setRecentMessages] = useState([]);
    const [notifications, setNotifications] = useState([]);
    const [showNotificationPanel, setShowNotificationPanel] = useState(false);
    const lastMessageCount = useRef(0);

    const fetchStats = useCallback(() => {
        try {
            // Get local stats
            const localNews = JSON.parse(localStorage.getItem('siteNews') || '[]');
            const localAds = JSON.parse(localStorage.getItem('siteAds') || '[]');
            const localMessages = JSON.parse(localStorage.getItem(MESSAGES_KEY) || '[]');

            const unreadCount = localMessages.filter(m => !m.read).length;

            // Check for new messages
            if (localMessages.length > lastMessageCount.current && lastMessageCount.current > 0) {
                const newMessages = localMessages.slice(0, localMessages.length - lastMessageCount.current);
                newMessages.forEach(msg => {
                    addNotification({
                        id: Date.now(),
                        type: msg.type,
                        title: msg.type === 'application' ? 'New Application!' : 'New Message!',
                        message: `${msg.name} - ${msg.subject || msg.productTitle || 'No subject'}`,
                        time: new Date().toLocaleTimeString()
                    });
                });
            }
            lastMessageCount.current = localMessages.length;

            setStats({
                news: localNews.length,
                contacts: localMessages.length,
                ads: localAds.filter(a => a.active).length,
                unread_contacts: unreadCount
            });

            // Get recent unread messages
            setRecentMessages(localMessages.filter(m => !m.read).slice(0, 5));

            setLastUpdate(new Date());
        } catch (error) {
            console.error('Failed to fetch stats:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    const addNotification = useCallback((notification) => {
        setNotifications(prev => [notification, ...prev].slice(0, 10));
        setShowNotificationPanel(true);

        // Auto hide after 5 seconds
        setTimeout(() => {
            setNotifications(prev => prev.filter(n => n.id !== notification.id));
        }, 5000);
    }, []);

    useEffect(() => {
        if (!authLoading && !user) {
            navigate('/admin/login');
            return;
        }

        if (user) {
            fetchStats();

            // Real-time updates every 3 seconds
            const interval = setInterval(fetchStats, 3000);

            // Listen for new message events
            const handleNewMessage = (event) => {
                const msg = event.detail;
                addNotification({
                    id: Date.now(),
                    type: msg.type,
                    title: msg.type === 'application' ? '📋 New Application!' : '💬 New Message!',
                    message: `From: ${msg.name}`,
                    time: new Date().toLocaleTimeString()
                });
                fetchStats();
            };

            window.addEventListener('newMessage', handleNewMessage);

            // Listen for storage changes from other tabs
            const handleStorage = (e) => {
                if (e.key === MESSAGES_KEY) {
                    fetchStats();
                }
            };
            window.addEventListener('storage', handleStorage);

            return () => {
                clearInterval(interval);
                window.removeEventListener('newMessage', handleNewMessage);
                window.removeEventListener('storage', handleStorage);
            };
        }
    }, [user, authLoading, navigate, fetchStats, addNotification]);

    if (authLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center bg-slate-900">
                <div className="text-center">
                    <Loader2 className="w-10 h-10 animate-spin text-teal-500 mx-auto mb-4" />
                    <p className="text-gray-400">Loading dashboard...</p>
                </div>
            </div>
        );
    }

    const statsCards = [
        {
            title: 'Published News',
            value: stats.news,
            icon: Newspaper,
            gradient: 'from-purple-500 to-indigo-500',
            shadow: 'shadow-purple-500/25',
            link: '/admin/news'
        },
        {
            title: 'Active Ads',
            value: stats.ads,
            icon: TrendingUp,
            gradient: 'from-emerald-500 to-teal-500',
            shadow: 'shadow-emerald-500/25',
            link: '/admin/ads'
        },
        {
            title: 'Messages',
            value: stats.contacts,
            icon: MessageSquare,
            gradient: 'from-amber-500 to-orange-500',
            shadow: 'shadow-amber-500/25',
            link: '/admin/contacts',
            badge: stats.unread_contacts > 0 ? stats.unread_contacts : null
        }
    ];

    const quickActions = [
        {
            title: 'Create News Article',
            description: 'Write and publish a new article',
            icon: Newspaper,
            gradient: 'from-purple-500 to-indigo-500',
            link: '/admin/news/new'
        },
        {
            title: 'Manage Branches',
            description: 'Add or edit branch locations',
            icon: Activity,
            gradient: 'from-teal-500 to-cyan-500',
            link: '/admin/branches'
        },
        {
            title: 'Manage Advertisements',
            description: 'Update website banners and ads',
            icon: TrendingUp,
            gradient: 'from-emerald-500 to-teal-500',
            link: '/admin/ads'
        },
        {
            title: 'View Website',
            description: 'See your live website',
            icon: Eye,
            gradient: 'from-blue-500 to-cyan-500',
            link: '/',
            external: true
        }
    ];

    return (
        <AdminLayout title="Dashboard">
            <Helmet>
                <title>Admin Dashboard - Pride Path Commercial</title>
            </Helmet>

            {/* Notification Toast */}
            {notifications.length > 0 && (
                <div className="fixed top-4 right-4 z-50 space-y-2">
                    {notifications.map(notification => (
                        <div
                            key={notification.id}
                            className="bg-gradient-to-r from-teal-500 to-blue-500 text-white px-6 py-4 rounded-xl shadow-2xl animate-slideIn flex items-start gap-3 min-w-[300px]"
                            style={{ animation: 'slideIn 0.3s ease-out' }}
                        >
                            <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center flex-shrink-0">
                                {notification.type === 'application' ? (
                                    <FileText className="w-5 h-5" />
                                ) : (
                                    <Mail className="w-5 h-5" />
                                )}
                            </div>
                            <div className="flex-1">
                                <p className="font-bold">{notification.title}</p>
                                <p className="text-sm text-white/80">{notification.message}</p>
                                <p className="text-xs text-white/60 mt-1">{notification.time}</p>
                            </div>
                            <button
                                onClick={() => setNotifications(prev => prev.filter(n => n.id !== notification.id))}
                                className="text-white/60 hover:text-white"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* Welcome Card */}
            <div className="bg-gradient-to-r from-teal-500/20 to-blue-500/20 backdrop-blur-xl rounded-2xl p-6 mb-8 border border-white/10">
                <div className="flex items-center justify-between flex-wrap gap-4">
                    <div>
                        <div className="flex items-center gap-2 mb-2">
                            <Sparkles className="w-5 h-5 text-teal-400" />
                            <span className="text-teal-400 text-sm font-medium">Welcome back!</span>
                        </div>
                        <h2 className="text-2xl font-bold text-white mb-1">Admin Dashboard</h2>
                        <p className="text-gray-400 text-sm">Manage your website content in real-time</p>
                    </div>
                    <div className="flex items-center gap-4">
                        {/* Notification Bell */}
                        <button
                            onClick={() => setShowNotificationPanel(!showNotificationPanel)}
                            className="relative p-3 bg-white/5 rounded-xl border border-white/10 hover:bg-white/10 transition-all"
                        >
                            <Bell className="w-5 h-5 text-gray-400" />
                            {stats.unread_contacts > 0 && (
                                <span className="absolute -top-1 -right-1 w-5 h-5 bg-red-500 text-white text-xs font-bold rounded-full flex items-center justify-center animate-pulse">
                                    {stats.unread_contacts}
                                </span>
                            )}
                        </button>

                        {/* Live Indicator */}
                        <div className="flex items-center gap-2 px-4 py-2 bg-white/5 rounded-xl border border-white/10">
                            <div className="relative flex h-2 w-2">
                                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
                            </div>
                            <span className="text-sm text-gray-400">Live • {lastUpdate.toLocaleTimeString()}</span>
                            <Activity className="w-4 h-4 text-emerald-400" />
                        </div>
                    </div>
                </div>
            </div>

            {/* Stats Grid */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                {statsCards.map((card, index) => (
                    <Link
                        key={index}
                        to={card.link}
                        className="group relative bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-white/20 transition-all hover:scale-[1.02]"
                    >
                        <div className="flex items-center justify-between mb-4">
                            <div className={`w-14 h-14 bg-gradient-to-br ${card.gradient} rounded-xl flex items-center justify-center shadow-lg ${card.shadow}`}>
                                <card.icon className="w-7 h-7 text-white" />
                            </div>
                            <div className="text-right">
                                {loading ? (
                                    <div className="h-10 w-16 bg-white/10 animate-pulse rounded-lg"></div>
                                ) : (
                                    <span className="text-4xl font-bold text-white">{card.value}</span>
                                )}
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <h3 className="text-gray-400 font-medium">{card.title}</h3>
                            {card.badge && (
                                <span className="px-2 py-1 bg-red-500 text-white text-xs font-bold rounded-full animate-pulse">
                                    {card.badge} new
                                </span>
                            )}
                            <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-white group-hover:translate-x-1 transition-all" />
                        </div>
                    </Link>
                ))}
            </div>

            {/* Unread Messages Section */}
            {recentMessages.length > 0 && (
                <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10 mb-8">
                    <div className="flex items-center justify-between mb-4">
                        <h3 className="text-lg font-bold text-white flex items-center gap-2">
                            <AlertCircle className="w-5 h-5 text-red-400" />
                            Unread Messages ({stats.unread_contacts})
                        </h3>
                        <Link
                            to="/admin/contacts"
                            className="text-teal-400 text-sm hover:underline"
                        >
                            View All →
                        </Link>
                    </div>
                    <div className="space-y-3">
                        {recentMessages.map(msg => (
                            <Link
                                key={msg.id}
                                to="/admin/contacts"
                                className="flex items-center gap-4 p-4 bg-white/5 hover:bg-white/10 rounded-xl transition-all border-l-4 border-teal-500"
                            >
                                <div className={`w-12 h-12 rounded-full flex items-center justify-center ${msg.type === 'application'
                                    ? 'bg-purple-500/20 text-purple-400'
                                    : 'bg-teal-500/20 text-teal-400'
                                    }`}>
                                    {msg.type === 'application' ? (
                                        <FileText className="w-5 h-5" />
                                    ) : (
                                        <User className="w-5 h-5" />
                                    )}
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center justify-between mb-1">
                                        <p className="font-medium text-white truncate">{msg.name}</p>
                                        <span className="text-xs text-gray-500 flex-shrink-0 ml-2">
                                            {new Date(msg.created_at).toLocaleTimeString()}
                                        </span>
                                    </div>
                                    <p className="text-sm text-gray-400 truncate">
                                        {msg.subject || msg.productTitle || 'No subject'}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate mt-1">{msg.message}</p>
                                </div>
                                <span className={`px-2.5 py-1 rounded-full text-xs font-medium flex-shrink-0 ${msg.type === 'application'
                                    ? 'bg-purple-500/20 text-purple-300 border border-purple-500/30'
                                    : 'bg-teal-500/20 text-teal-300 border border-teal-500/30'
                                    }`}>
                                    {msg.type === 'application' ? 'Application' : 'Contact'}
                                </span>
                            </Link>
                        ))}
                    </div>
                </div>
            )}

            {/* Quick Actions */}
            <div className="bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Plus className="w-5 h-5 text-teal-400" />
                    Quick Actions
                </h3>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {quickActions.map((action, index) => (
                        <Link
                            key={index}
                            to={action.link}
                            target={action.external ? '_blank' : undefined}
                            className="group p-5 bg-white/5 rounded-xl border border-white/10 hover:border-white/20 hover:bg-white/10 transition-all"
                        >
                            <div className="flex items-center justify-between mb-3">
                                <div className={`w-12 h-12 bg-gradient-to-br ${action.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                                    <action.icon className="w-6 h-6 text-white" />
                                </div>
                                <ChevronRight className="w-5 h-5 text-gray-600 group-hover:text-white group-hover:translate-x-1 transition-all" />
                            </div>
                            <h4 className="font-semibold text-white mb-1">{action.title}</h4>
                            <p className="text-sm text-gray-500">{action.description}</p>
                        </Link>
                    ))}
                </div>
            </div>

            {/* Recent Activity */}
            <div className="mt-8 bg-white/5 backdrop-blur-xl rounded-2xl p-6 border border-white/10">
                <h3 className="text-lg font-bold text-white mb-4 flex items-center gap-2">
                    <Clock className="w-5 h-5 text-blue-400" />
                    System Status
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
                        <div className="w-3 h-3 bg-emerald-500 rounded-full animate-pulse"></div>
                        <div>
                            <p className="text-white text-sm font-medium">Auto-refresh Active</p>
                            <p className="text-gray-500 text-xs">Updating every 3 seconds</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
                        <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                        <div>
                            <p className="text-white text-sm font-medium">Real-time Notifications</p>
                            <p className="text-gray-500 text-xs">Enabled for new messages</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3 p-4 bg-white/5 rounded-xl">
                        <div className="w-3 h-3 bg-purple-500 rounded-full"></div>
                        <div>
                            <p className="text-white text-sm font-medium">Last Updated</p>
                            <p className="text-gray-500 text-xs">{lastUpdate.toLocaleString()}</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* CSS for slide animation */}
            <style>{`
                @keyframes slideIn {
                    from {
                        opacity: 0;
                        transform: translateX(100px);
                    }
                    to {
                        opacity: 1;
                        transform: translateX(0);
                    }
                }
            `}</style>
        </AdminLayout>
    );
};

export default AdminDashboardPage;
